// Channel Binding API client — thin wrapper around sam-dbam /deployments/{id}/channels
import { getKernelApiBase, getOrgContext } from './cloudApi';

/** Build org-context headers (mirrors cloudApi.httpRequest behaviour). */
function orgHeaders(extra = {}) {
    const ctx = getOrgContext();
    const h = { ...extra };
    if (ctx.orgId) h['X-Org-ID'] = ctx.orgId;
    if (ctx.userId) h['X-User-ID'] = ctx.userId;
    return h;
}

/**
 * List channel bindings for a deployment.
 * @param {string} deploymentId
 * @returns {Promise<Array>} array of ChannelBinding objects
 */
export async function listChannelBindings(deploymentId) {
    if (!deploymentId) return [];
    const base = getKernelApiBase().replace(/\/$/, '');
    const url = `${base}/deployments/${encodeURIComponent(deploymentId)}/channels`;
    const resp = await fetch(url, { headers: orgHeaders() });
    if (!resp.ok) {
        if (resp.status === 404) return [];
        const text = await resp.text().catch(() => '');
        throw new Error(`List channels failed (${resp.status}): ${text || resp.statusText}`);
    }
    const json = await resp.json();
    // sam-dbam wraps as {data: [...]} or returns array directly
    return Array.isArray(json) ? json : (Array.isArray(json?.data) ? json.data : []);
}

/**
 * Create a new channel binding on a deployment.
 * @param {string} deploymentId
 * @param {{ channel_type: string, enabled?: boolean, entry_point?: string, channel_config?: object }} body
 * @returns {Promise<object>} created ChannelBinding
 */
export async function createChannelBinding(deploymentId, body) {
    const base = getKernelApiBase().replace(/\/$/, '');
    const url = `${base}/deployments/${encodeURIComponent(deploymentId)}/channels`;
    const resp = await fetch(url, {
        method: 'POST',
        headers: orgHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
    });
    if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Create channel failed (${resp.status}): ${text || resp.statusText}`);
    }
    const json = await resp.json();
    return json.data || json;
}

/**
 * Update an existing channel binding.
 * @param {string} deploymentId
 * @param {string} channelType  e.g. 'browser', 'sms', 'whatsapp', 'messenger'
 * @param {{ enabled?: boolean, entry_point?: string, channel_config?: object }} body
 * @returns {Promise<object>} updated ChannelBinding
 */
export async function updateChannelBinding(deploymentId, channelType, body) {
    const base = getKernelApiBase().replace(/\/$/, '');
    const url = `${base}/deployments/${encodeURIComponent(deploymentId)}/channels/${encodeURIComponent(channelType)}`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers: orgHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
    });
    if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Update channel failed (${resp.status}): ${text || resp.statusText}`);
    }
    const json = await resp.json();
    return json.data || json;
}

/**
 * Delete a channel binding.
 * @param {string} deploymentId
 * @param {string} channelType
 * @returns {Promise<void>}
 */
export async function deleteChannelBinding(deploymentId, channelType) {
    const base = getKernelApiBase().replace(/\/$/, '');
    const url = `${base}/deployments/${encodeURIComponent(deploymentId)}/channels/${encodeURIComponent(channelType)}`;
    const resp = await fetch(url, { method: 'DELETE', headers: orgHeaders() });
    if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Delete channel failed (${resp.status}): ${text || resp.statusText}`);
    }
}
